import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import SiteShell from "@/components/layout/SiteShell";
import Home from "@/pages/Home";
import Events from "@/pages/Events";
import HireUs from "@/pages/HireUs";
import Contact from "@/pages/Contact";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { AdminAuthProvider } from "@/admin/AuthContext";
import { ProtectedAdminRoute } from "@/admin/ProtectedAdminRoute";
import { AdminShell } from "@/admin/AdminShell";
import AdminLogin from "@/admin/pages/AdminLogin";
import AdminDashboard from "@/admin/pages/AdminDashboard";
import AdminEvents from "@/admin/pages/AdminEvents";
import AdminEventForm from "@/admin/pages/AdminEventForm";
import AdminSubfolders from "@/admin/pages/AdminSubfolders";
import AdminCustomers from "@/admin/pages/AdminCustomers";
import AdminCredits from "@/admin/pages/AdminCredits";
import AdminContacts from "@/admin/pages/AdminContacts";
import AdminHires from "@/admin/pages/AdminHires";
import AdminSettings from "@/admin/pages/AdminSettings";
import AdminPhotos from "@/admin/pages/AdminPhotos";
import AdminEventPricing from "@/admin/pages/AdminEventPricing";
import AdminPurchases from "@/admin/pages/AdminPurchases";
import AdminDownloads from "@/admin/pages/AdminDownloads";
import AdminSecurityEvents from "@/admin/pages/AdminSecurityEvents";
import { CustomerAuthProvider } from "@/customer/AuthContext";
import { CartProvider } from "@/customer/CartContext";
import { ProtectedCustomerRoute } from "@/customer/ProtectedCustomerRoute";
import CustomerDashboard from "@/customer/pages/CustomerDashboard";
import CustomerProfile from "@/customer/pages/CustomerProfile";
import CustomerCredits from "@/customer/pages/CustomerCredits";
import CustomerCart from "@/customer/pages/CustomerCart";
import CustomerPurchased from "@/customer/pages/CustomerPurchased";
import CustomerPurchases from "@/customer/pages/CustomerPurchases";
import CustomerDownloads from "@/customer/pages/CustomerDownloads";
import EventDetail from "@/pages/EventDetail";
import EventGallery from "@/pages/EventGallery";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
};

const Public = ({ children }) => <SiteShell>{children}</SiteShell>;

const AdminProtected = ({ children }) => (
  <ProtectedAdminRoute>
    <AdminShell>{children}</AdminShell>
  </ProtectedAdminRoute>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <CustomerAuthProvider>
        <CartProvider>
        <AdminAuthProvider>
          <ScrollToTop />
          <Routes>
            {/* Public */}
            <Route path="/" element={<Public><Home /></Public>} />
            <Route path="/events" element={<Public><Events /></Public>} />
            <Route path="/hire" element={<Public><HireUs /></Public>} />
            <Route path="/contact" element={<Public><Contact /></Public>} />
            <Route path="/auth" element={<Public><Auth /></Public>} />
            <Route path="/forgot-password" element={<Public><ForgotPassword /></Public>} />
            <Route path="/reset-password" element={<Public><ResetPassword /></Public>} />
            <Route path="/events/:slug" element={<Public><EventDetail /></Public>} />
            <Route path="/events/:slug/:sid" element={<Public><EventGallery /></Public>} />
            <Route path="/customer" element={<Public><ProtectedCustomerRoute><CustomerDashboard /></ProtectedCustomerRoute></Public>} />
            <Route path="/customer/profile" element={<Public><ProtectedCustomerRoute><CustomerProfile /></ProtectedCustomerRoute></Public>} />
            <Route path="/customer/credits" element={<Public><ProtectedCustomerRoute><CustomerCredits /></ProtectedCustomerRoute></Public>} />
            <Route path="/customer/cart" element={<Public><ProtectedCustomerRoute><CustomerCart /></ProtectedCustomerRoute></Public>} />
            <Route path="/customer/purchased" element={<Public><ProtectedCustomerRoute><CustomerPurchased /></ProtectedCustomerRoute></Public>} />
            <Route path="/customer/purchases" element={<Public><ProtectedCustomerRoute><CustomerPurchases /></ProtectedCustomerRoute></Public>} />
            <Route path="/customer/downloads" element={<Public><ProtectedCustomerRoute><CustomerDownloads /></ProtectedCustomerRoute></Public>} />

            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminProtected><AdminDashboard /></AdminProtected>} />
            <Route path="/admin/events" element={<AdminProtected><AdminEvents /></AdminProtected>} />
            <Route path="/admin/events/new" element={<AdminProtected><AdminEventForm mode="create" /></AdminProtected>} />
            <Route path="/admin/events/:id/edit" element={<AdminProtected><AdminEventForm mode="edit" /></AdminProtected>} />
            <Route path="/admin/events/:id/folders" element={<AdminProtected><AdminSubfolders /></AdminProtected>} />
            <Route path="/admin/events/:id/folders/:sid/photos" element={<AdminProtected><AdminPhotos /></AdminProtected>} />
            <Route path="/admin/events/:id/pricing" element={<AdminProtected><AdminEventPricing /></AdminProtected>} />
            <Route path="/admin/purchases" element={<AdminProtected><AdminPurchases /></AdminProtected>} />
            <Route path="/admin/downloads" element={<AdminProtected><AdminDownloads /></AdminProtected>} />
            <Route path="/admin/security-events" element={<AdminProtected><AdminSecurityEvents /></AdminProtected>} />
            <Route path="/admin/customers" element={<AdminProtected><AdminCustomers /></AdminProtected>} />
            <Route path="/admin/credits" element={<AdminProtected><AdminCredits /></AdminProtected>} />
            <Route path="/admin/contacts" element={<AdminProtected><AdminContacts /></AdminProtected>} />
            <Route path="/admin/hires" element={<AdminProtected><AdminHires /></AdminProtected>} />
            <Route path="/admin/settings" element={<AdminProtected><AdminSettings /></AdminProtected>} />

            <Route path="*" element={<Public><Home /></Public>} />
          </Routes>
          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{
              style: {
                background: "rgba(10, 10, 14, 0.92)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                color: "#fff",
                backdropFilter: "blur(12px)",
              },
            }}
          />
        </AdminAuthProvider>
        </CartProvider>
        </CustomerAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
